import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { 
  Plus, TrendingUp, TrendingDown, Calendar, 
  Pencil, Trash2, Search, CreditCard, CheckCircle2,
  AlertCircle, Clock, Tag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContabilidadModuleAcademia = ({ academiaId, section }) => {
  const [loading, setLoading] = useState(true);
  const [ingresos, setIngresos] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const [cuentasPorPagar, setCuentasPorPagar] = useState([]);
  const [resumen, setResumen] = useState({ total_ingresos: 0, total_egresos: 0, balance: 0 });
  const [categoriasIngresos, setCategoriasIngresos] = useState([]);
  const [categoriasEgresos, setCategoriasEgresos] = useState([]);
  
  const [showDialog, setShowDialog] = useState(false);
  const [showCategoriaDialog, setShowCategoriaDialog] = useState(false);
  const [dialogType, setDialogType] = useState('ingreso');
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [form, setForm] = useState({
    descripcion: '',
    monto: '',
    categoria: '',
    fecha: new Date().toISOString().split('T')[0],
    comprobante: '',
    notas: '',
    proveedor: '',
    fecha_vencimiento: '',
    recurrente: false,
    frecuencia: 'mensual',
    en_cuotas: false,
    numero_cuotas: 3,
    cuotas: []
  });

  const [nuevaCategoria, setNuevaCategoria] = useState({ nombre: '', tipo: 'ingreso', color: 'slate' });

  useEffect(() => {
    if (academiaId) loadData();
  }, [academiaId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ingresosRes, egresosRes, cuentasRes, resumenRes, catIngRes, catEgrRes] = await Promise.all([
        axios.get(`${API}/academias/${academiaId}/contabilidad/ingresos`, { withCredentials: true }),
        axios.get(`${API}/academias/${academiaId}/contabilidad/egresos`, { withCredentials: true }),
        axios.get(`${API}/academias/${academiaId}/contabilidad/cuentas-por-pagar`, { withCredentials: true }),
        axios.get(`${API}/academias/${academiaId}/contabilidad/resumen`, { withCredentials: true }),
        axios.get(`${API}/academias/${academiaId}/contabilidad/categorias?tipo=ingreso`, { withCredentials: true }),
        axios.get(`${API}/academias/${academiaId}/contabilidad/categorias?tipo=egreso`, { withCredentials: true })
      ]);
      setIngresos(ingresosRes.data);
      setEgresos(egresosRes.data);
      setCuentasPorPagar(cuentasRes.data);
      setResumen(resumenRes.data);
      setCategoriasIngresos(catIngRes.data);
      setCategoriasEgresos(catEgrRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      // Si no hay endpoints aún, mostrar datos vacíos
      setResumen({ ingresos_mes: 0, egresos_mes: 0, cuentas_pendientes: 0 });
      setIngresos([]);
      setEgresos([]);
      setCuentasPorPagar([]);
      setCategoriasIngresos([]);
      setCategoriasEgresos([]);
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const resetForm = () => {
    setForm({
      descripcion: '',
      monto: '',
      categoria: '',
      fecha: new Date().toISOString().split('T')[0],
      comprobante: '',
      notas: '',
      proveedor: '',
      fecha_vencimiento: '',
      recurrente: false,
      frecuencia: 'mensual',
      en_cuotas: false,
      numero_cuotas: 3,
      cuotas: []
    });
    setEditingItem(null);
  };

  const generarCuotas = (montoTotal, numCuotas, fechaInicio) => {
    const montoCuota = Math.round(montoTotal / numCuotas);
    const cuotas = [];
    const fecha = new Date(fechaInicio + 'T12:00:00');
    
    for (let i = 0; i < numCuotas; i++) {
      const fechaCuota = new Date(fecha);
      fechaCuota.setMonth(fechaCuota.getMonth() + i);
      cuotas.push({
        numero: i + 1,
        monto: montoCuota,
        fecha_vencimiento: fechaCuota.toISOString().split('T')[0]
      });
    }
    
    const sumaCuotas = cuotas.reduce((acc, c) => acc + c.monto, 0);
    if (sumaCuotas !== montoTotal) {
      cuotas[numCuotas - 1].monto += (montoTotal - sumaCuotas);
    }
    
    return cuotas;
  };

  const handleOpenDialog = (type, item = null) => {
    setDialogType(type);
    if (item) {
      setEditingItem(item);
      setForm({
        descripcion: item.descripcion || '',
        monto: item.monto?.toString() || '',
        categoria: item.categoria || '',
        fecha: item.fecha || new Date().toISOString().split('T')[0],
        comprobante: item.comprobante || '',
        notas: item.notas || '',
        proveedor: item.proveedor || '',
        fecha_vencimiento: item.fecha_vencimiento || '',
        recurrente: item.recurrente || false,
        frecuencia: item.frecuencia || 'mensual',
        en_cuotas: false,
        numero_cuotas: 3,
        cuotas: []
      });
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...form,
        monto: parseFloat(form.monto)
      };

      if (dialogType === 'ingreso') {
        if (editingItem) {
          await axios.put(`${API}/academias/${academiaId}/contabilidad/ingresos/${editingItem.id}`, data, { withCredentials: true });
          toast.success('Ingreso actualizado');
        } else {
          await axios.post(`${API}/academias/${academiaId}/contabilidad/ingresos`, data, { withCredentials: true });
          toast.success('Ingreso registrado');
        }
      } else if (dialogType === 'egreso') {
        if (editingItem) {
          await axios.put(`${API}/academias/${academiaId}/contabilidad/egresos/${editingItem.id}`, data, { withCredentials: true });
          toast.success('Egreso actualizado');
        } else {
          await axios.post(`${API}/academias/${academiaId}/contabilidad/egresos`, data, { withCredentials: true });
          toast.success('Egreso registrado');
        }
      } else if (dialogType === 'cuenta') {
        if (editingItem) {
          await axios.put(`${API}/academias/${academiaId}/contabilidad/cuentas-por-pagar/${editingItem.id}`, data, { withCredentials: true });
          toast.success('Cuenta actualizada');
        } else {
          if (form.en_cuotas && form.cuotas.length > 0) {
            data.en_cuotas = true;
            data.cuotas = form.cuotas.map(c => ({
              monto: c.monto,
              fecha_vencimiento: c.fecha_vencimiento
            }));
          }
          await axios.post(`${API}/academias/${academiaId}/contabilidad/cuentas-por-pagar`, data, { withCredentials: true });
          toast.success(form.en_cuotas ? `Cuenta creada en ${form.cuotas.length} cuotas` : 'Cuenta registrada');
        }
      }

      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try {
      await axios.delete(`${API}/academias/${academiaId}/contabilidad/${type}/${id}`, { withCredentials: true });
      toast.success('Registro eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleMarcarPagada = async (id) => {
    try {
      await axios.put(`${API}/academias/${academiaId}/contabilidad/cuentas-por-pagar/${id}/pagar`, {}, { withCredentials: true });
      toast.success('Cuenta marcada como pagada');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleCrearCategoria = async () => {
    try {
      await axios.post(`${API}/academias/${academiaId}/contabilidad/categorias`, nuevaCategoria, { withCredentials: true });
      toast.success('Categoría creada');
      setShowCategoriaDialog(false);
      setNuevaCategoria({ nombre: '', tipo: 'ingreso', color: 'slate' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear categoría');
    }
  };

  const getCategoriaColor = (categoriaId, tipo) => {
    const cats = tipo === 'ingreso' ? categoriasIngresos : categoriasEgresos;
    const cat = cats.find(c => c.id === categoriaId);
    return cat?.color || 'slate';
  };

  const getCategoriaNombre = (categoriaId, tipo) => {
    const cats = tipo === 'ingreso' ? categoriasIngresos : categoriasEgresos;
    const cat = cats.find(c => c.id === categoriaId);
    return cat?.nombre || categoriaId;
  };

  const filterItems = (items) => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(item => 
      item.descripcion?.toLowerCase().includes(q) ||
      item.categoria?.toLowerCase().includes(q) ||
      item.proveedor?.toLowerCase().includes(q)
    );
  };

  const CategoriaSelector = ({ tipo, value, onChange }) => {
    const categorias = tipo === 'ingreso' ? categoriasIngresos : categoriasEgresos;
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300">Categoría</Label>
          <Button 
            type="button" 
            size="sm" 
            variant="ghost" 
            onClick={() => { setNuevaCategoria(prev => ({ ...prev, tipo })); setShowCategoriaDialog(true); }}
            className="text-xs text-emerald-400 hover:text-emerald-300"
          >
            <Plus className="w-3 h-3 mr-1" /> Agregar
          </Button>
        </div>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
            <SelectValue placeholder="Seleccionar categoría..." />
          </SelectTrigger>
          <SelectContent>
            {categorias.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>
                <span className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full bg-${cat.color}-500`}></span>
                  {cat.nombre}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const CategoriaDialog = () => (
    <Dialog open={showCategoriaDialog} onOpenChange={setShowCategoriaDialog}>
      <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Nueva Categoría
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-slate-300">Nombre</Label>
            <Input
              value={nuevaCategoria.nombre}
              onChange={(e) => setNuevaCategoria(prev => ({ ...prev, nombre: e.target.value }))}
              placeholder="Ej: Mensualidades, Inscripciones..."
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>
          <div>
            <Label className="text-slate-300">Tipo</Label>
            <Select value={nuevaCategoria.tipo} onValueChange={(v) => setNuevaCategoria(prev => ({ ...prev, tipo: v }))}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ingreso">Ingreso</SelectItem>
                <SelectItem value="egreso">Egreso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Color</Label>
            <Select value={nuevaCategoria.color} onValueChange={(v) => setNuevaCategoria(prev => ({ ...prev, color: v }))}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="green"><span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-green-500"></span>Verde</span></SelectItem>
                <SelectItem value="blue"><span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-blue-500"></span>Azul</span></SelectItem>
                <SelectItem value="red"><span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-500"></span>Rojo</span></SelectItem>
                <SelectItem value="emerald"><span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-500"></span>Esmeralda</span></SelectItem>
                <SelectItem value="purple"><span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-purple-500"></span>Morado</span></SelectItem>
                <SelectItem value="orange"><span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-orange-500"></span>Naranja</span></SelectItem>
                <SelectItem value="slate"><span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-slate-500"></span>Gris</span></SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowCategoriaDialog(false)} className="flex-1 border-slate-600">
              Cancelar
            </Button>
            <Button onClick={handleCrearCategoria} className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={!nuevaCategoria.nombre}>
              Crear Categoría
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // INGRESOS Section
  if (section === 'ingresos') {
    const filtered = filterItems(ingresos);
    return (
      <div className="space-y-6">
        <CategoriaDialog />
        
        <Card className="p-4 bg-emerald-500/20 border-emerald-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-emerald-400" />
              <div>
                <p className="text-sm text-emerald-300">Total Ingresos (este mes)</p>
                <p className="text-2xl font-bold text-white">{formatMoney(resumen.ingresos_mes || 0)}</p>
              </div>
            </div>
            <Button onClick={() => handleOpenDialog('ingreso')} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Ingreso
            </Button>
          </div>
        </Card>

        <div className="relative">
          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar ingresos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-700 border-slate-600 text-white"
          />
        </div>

        <div className="space-y-3">
          {filtered.length === 0 ? (
            <Card className="p-8 bg-slate-800 border-slate-700 text-center">
              <TrendingUp className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">No hay ingresos registrados</p>
            </Card>
          ) : (
            filtered.map(item => (
              <Card key={item.id} className="p-4 bg-slate-800 border-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-white font-medium">{item.descripcion}</h3>
                      <Badge className={`bg-${getCategoriaColor(item.categoria, 'ingreso')}-500/20 text-${getCategoriaColor(item.categoria, 'ingreso')}-400`}>
                        {getCategoriaNombre(item.categoria, 'ingreso')}
                      </Badge>
                    </div>
                    <div className="flex gap-4 mt-1 text-sm text-slate-400">
                      <span><Calendar className="w-3 h-3 inline mr-1" />{item.fecha}</span>
                      {item.comprobante && <span>#{item.comprobante}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-xl font-bold text-emerald-400">{formatMoney(item.monto)}</p>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleOpenDialog('ingreso', item)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete('ingresos', item.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        <Dialog open={showDialog && dialogType === 'ingreso'} onOpenChange={setShowDialog}>
          <DialogContent className="bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">{editingItem ? 'Editar Ingreso' : 'Nuevo Ingreso'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Descripción</Label>
                <Input
                  value={form.descripcion}
                  onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Ej: Inscripción alumno"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Monto</Label>
                  <Input
                    type="number"
                    value={form.monto}
                    onChange={(e) => setForm(prev => ({ ...prev, monto: e.target.value }))}
                    placeholder="0"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Fecha</Label>
                  <Input
                    type="date"
                    value={form.fecha}
                    onChange={(e) => setForm(prev => ({ ...prev, fecha: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <CategoriaSelector tipo="ingreso" value={form.categoria} onChange={(v) => setForm(prev => ({ ...prev, categoria: v }))} />
              <div>
                <Label className="text-slate-300">Comprobante (opcional)</Label>
                <Input
                  value={form.comprobante}
                  onChange={(e) => setForm(prev => ({ ...prev, comprobante: e.target.value }))}
                  placeholder="Número de comprobante"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Notas (opcional)</Label>
                <Textarea
                  value={form.notas}
                  onChange={(e) => setForm(prev => ({ ...prev, notas: e.target.value }))}
                  placeholder="Notas adicionales..."
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1 border-slate-600">
                  Cancelar
                </Button>
                <Button onClick={handleSubmit} className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={!form.descripcion || !form.monto || !form.categoria}>
                  {editingItem ? 'Guardar' : 'Registrar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // EGRESOS Section
  if (section === 'egresos') {
    const filtered = filterItems(egresos);
    return (
      <div className="space-y-6">
        <CategoriaDialog />
        
        <Card className="p-4 bg-red-500/20 border-red-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingDown className="w-8 h-8 text-red-400" />
              <div>
                <p className="text-sm text-red-300">Total Egresos (este mes)</p>
                <p className="text-2xl font-bold text-white">{formatMoney(resumen.egresos_mes || 0)}</p>
              </div>
            </div>
            <Button onClick={() => handleOpenDialog('egreso')} className="bg-red-600 hover:bg-red-700">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Egreso
            </Button>
          </div>
        </Card>

        <div className="relative">
          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar egresos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-700 border-slate-600 text-white"
          />
        </div>

        <div className="space-y-3">
          {filtered.length === 0 ? (
            <Card className="p-8 bg-slate-800 border-slate-700 text-center">
              <TrendingDown className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">No hay egresos registrados</p>
            </Card>
          ) : (
            filtered.map(item => (
              <Card key={item.id} className="p-4 bg-slate-800 border-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-white font-medium">{item.descripcion}</h3>
                      <Badge className={`bg-${getCategoriaColor(item.categoria, 'egreso')}-500/20 text-${getCategoriaColor(item.categoria, 'egreso')}-400`}>
                        {getCategoriaNombre(item.categoria, 'egreso')}
                      </Badge>
                    </div>
                    <div className="flex gap-4 mt-1 text-sm text-slate-400">
                      <span><Calendar className="w-3 h-3 inline mr-1" />{item.fecha}</span>
                      {item.comprobante && <span>#{item.comprobante}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-xl font-bold text-red-400">-{formatMoney(item.monto)}</p>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleOpenDialog('egreso', item)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete('egresos', item.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        <Dialog open={showDialog && dialogType === 'egreso'} onOpenChange={setShowDialog}>
          <DialogContent className="bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">{editingItem ? 'Editar Egreso' : 'Nuevo Egreso'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Descripción</Label>
                <Input
                  value={form.descripcion}
                  onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Ej: Compra balones"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Monto</Label>
                  <Input
                    type="number"
                    value={form.monto}
                    onChange={(e) => setForm(prev => ({ ...prev, monto: e.target.value }))}
                    placeholder="0"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Fecha</Label>
                  <Input
                    type="date"
                    value={form.fecha}
                    onChange={(e) => setForm(prev => ({ ...prev, fecha: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <CategoriaSelector tipo="egreso" value={form.categoria} onChange={(v) => setForm(prev => ({ ...prev, categoria: v }))} />
              <div>
                <Label className="text-slate-300">Comprobante (opcional)</Label>
                <Input
                  value={form.comprobante}
                  onChange={(e) => setForm(prev => ({ ...prev, comprobante: e.target.value }))}
                  placeholder="Número de boleta/factura"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Notas (opcional)</Label>
                <Textarea
                  value={form.notas}
                  onChange={(e) => setForm(prev => ({ ...prev, notas: e.target.value }))}
                  placeholder="Notas adicionales..."
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1 border-slate-600">
                  Cancelar
                </Button>
                <Button onClick={handleSubmit} className="flex-1 bg-red-600 hover:bg-red-700" disabled={!form.descripcion || !form.monto || !form.categoria}>
                  {editingItem ? 'Guardar' : 'Registrar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // CUENTAS POR PAGAR Section
  if (section === 'cuentas') {
    const pendientes = cuentasPorPagar.filter(c => c.estado !== 'pagada');
    const vencidas = pendientes.filter(c => c.fecha_vencimiento < new Date().toISOString().split('T')[0]);
    
    return (
      <div className="space-y-6">
        <CategoriaDialog />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4 bg-amber-500/20 border-amber-500/30">
            <div className="flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-amber-400" />
              <div>
                <p className="text-sm text-amber-300">Total Pendiente</p>
                <p className="text-2xl font-bold text-white">{formatMoney(resumen.cuentas_pendientes || 0)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-red-500/20 border-red-500/30">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <div>
                <p className="text-sm text-red-300">Vencidas</p>
                <p className="text-2xl font-bold text-white">{vencidas.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-slate-700 border-slate-600 flex items-center justify-center">
            <Button onClick={() => handleOpenDialog('cuenta')} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="w-4 h-4 mr-2" /> Nueva Cuenta
            </Button>
          </Card>
        </div>

        <div className="space-y-3">
          {pendientes.length === 0 ? (
            <Card className="p-8 bg-slate-800 border-slate-700 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
              <p className="text-slate-400">No hay cuentas pendientes</p>
            </Card>
          ) : (
            pendientes.map(item => {
              const esVencida = item.fecha_vencimiento < new Date().toISOString().split('T')[0];
              return (
                <Card key={item.id} className={`p-4 bg-slate-800 border-slate-700 ${esVencida ? 'border-l-4 border-l-red-500' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-white font-medium">{item.descripcion}</h3>
                        {esVencida && <Badge className="bg-red-500/20 text-red-400">Vencida</Badge>}
                        {item.recurrente && <Badge className="bg-blue-500/20 text-blue-400">Recurrente</Badge>}
                      </div>
                      <div className="flex gap-4 mt-1 text-sm text-slate-400">
                        {item.proveedor && <span>{item.proveedor}</span>}
                        <span><Clock className="w-3 h-3 inline mr-1" />Vence: {item.fecha_vencimiento}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-xl font-bold text-amber-400">{formatMoney(item.monto)}</p>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleMarcarPagada(item.id)} className="text-emerald-400 hover:text-emerald-300" title="Marcar como pagada">
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleOpenDialog('cuenta', item)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete('cuentas-por-pagar', item.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>

        <Dialog open={showDialog && dialogType === 'cuenta'} onOpenChange={setShowDialog}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">{editingItem ? 'Editar Cuenta' : 'Nueva Cuenta por Pagar'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Descripción</Label>
                <Input
                  value={form.descripcion}
                  onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Ej: Arriendo instalación"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Proveedor/Acreedor</Label>
                <Input
                  value={form.proveedor}
                  onChange={(e) => setForm(prev => ({ ...prev, proveedor: e.target.value }))}
                  placeholder="Ej: Municipalidad"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Monto</Label>
                  <Input
                    type="number"
                    value={form.monto}
                    onChange={(e) => setForm(prev => ({ ...prev, monto: e.target.value }))}
                    placeholder="0"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Fecha Vencimiento</Label>
                  <Input
                    type="date"
                    value={form.fecha_vencimiento}
                    onChange={(e) => setForm(prev => ({ ...prev, fecha_vencimiento: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <CategoriaSelector tipo="egreso" value={form.categoria} onChange={(v) => setForm(prev => ({ ...prev, categoria: v }))} />
              <div>
                <Label className="text-slate-300">Notas (opcional)</Label>
                <Textarea
                  value={form.notas}
                  onChange={(e) => setForm(prev => ({ ...prev, notas: e.target.value }))}
                  placeholder="Notas adicionales..."
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1 border-slate-600">
                  Cancelar
                </Button>
                <Button onClick={handleSubmit} className="flex-1 bg-amber-600 hover:bg-amber-700" disabled={!form.descripcion || !form.monto || !form.fecha_vencimiento}>
                  {editingItem ? 'Guardar' : 'Registrar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return null;
};

export default ContabilidadModuleAcademia;
